import { z } from 'zod';
import { Currency, FabricSubtype, PatternDirection } from '../enums';

export const MaterialLibrarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  category: z.string(),
  material_type: z.string().default('panel'),
  thickness: z.string().nullable(),
  unit: z.string().default('sheet'),
  sheet_width: z.number().default(48),
  sheet_length: z.number().default(96),
  cost_per_unit: z.number().default(0),
  currency: z.nativeEnum(Currency).default(Currency.USD),
  iva_percent: z.number().default(0),
  waste_factor: z.number().default(0.10),
  notes: z.string().nullable(),
  active: z.boolean().default(true),
  color: z.string().default('#c4a574'),
  texture_side1: z.string().nullable().optional(),
  texture_side2: z.string().nullable().optional(),
  texture_core: z.string().nullable().optional(),
  texture_side1_rotation: z.number().default(0),
  texture_side2_rotation: z.number().default(0),
  texture_core_rotation: z.number().default(0),
  texture_scale: z.number().default(1.0),
  texture_name: z.string().default(''),
  grain_direction: z.string().default('none'),
  vendor_code: z.string().default(''),
  finish: z.string().default(''),
  is_framing: z.boolean().default(true),
  side1_requires_finish: z.boolean().default(false),
  side2_requires_finish: z.boolean().default(false),
  nesting_border: z.number().default(0),
  nesting_spacing: z.number().default(0),
  nesting_cost_mode: z.enum(['whole_sheet', 'used_area']).default('whole_sheet'),
  cnc_cut: z.boolean().default(true),
  fabric_subtype: z.nativeEnum(FabricSubtype).nullable().optional(),
  pattern_direction: z.nativeEnum(PatternDirection).nullable().optional(),
  fabric_design_pct: z.number().min(0).default(0),
  roll_width: z.number().nullable().optional(),
  vendor: z.string().default(''),
  manufacturer: z.string().default(''),
  texture_profile_id_side1: z.string().uuid().nullable().optional(),
  texture_profile_id_side2: z.string().uuid().nullable().optional(),
  texture_profile_id_core: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateMaterialLibrarySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.string().min(1, 'Category is required'),
  material_type: z.string().default('panel'),
  thickness: z.string().nullable().optional(),
  unit: z.string().default('sheet'),
  sheet_width: z.number().default(48),
  sheet_length: z.number().default(96),
  cost_per_unit: z.number().default(0),
  currency: z.nativeEnum(Currency).default(Currency.USD),
  iva_percent: z.number().default(0),
  waste_factor: z.number().default(0.10),
  notes: z.string().nullable().optional(),
  active: z.boolean().default(true),
  color: z.string().default('#c4a574'),
  texture_side1: z.string().nullable().optional(),
  texture_side2: z.string().nullable().optional(),
  texture_core: z.string().nullable().optional(),
  texture_side1_rotation: z.number().default(0),
  texture_side2_rotation: z.number().default(0),
  texture_core_rotation: z.number().default(0),
  texture_scale: z.number().default(1.0),
  texture_name: z.string().default(''),
  grain_direction: z.string().default('none'),
  vendor_code: z.string().default(''),
  finish: z.string().default(''),
  is_framing: z.boolean().default(true),
  side1_requires_finish: z.boolean().default(false),
  side2_requires_finish: z.boolean().default(false),
  nesting_border: z.number().default(0),
  nesting_spacing: z.number().default(0),
  nesting_cost_mode: z.enum(['whole_sheet', 'used_area']).default('whole_sheet'),
  cnc_cut: z.boolean().default(true),
  fabric_subtype: z.nativeEnum(FabricSubtype).nullable().optional(),
  pattern_direction: z.nativeEnum(PatternDirection).nullable().optional(),
  fabric_design_pct: z.number().min(0).default(0),
  roll_width: z.number().nullable().optional(),
  vendor: z.string().default(''),
  manufacturer: z.string().default(''),
  texture_profile_id_side1: z.string().uuid().nullable().optional(),
  texture_profile_id_side2: z.string().uuid().nullable().optional(),
  texture_profile_id_core: z.string().uuid().nullable().optional(),
});

export const UpdateMaterialLibrarySchema = CreateMaterialLibrarySchema.partial();

export type MaterialLibrary = z.infer<typeof MaterialLibrarySchema>;
export type CreateMaterialLibrary = z.infer<typeof CreateMaterialLibrarySchema>;
export type UpdateMaterialLibrary = z.infer<typeof UpdateMaterialLibrarySchema>;
